import type { InternetOffersApiResponse } from "@/types/offers";

const OFFERS_BASE_URL =
  process.env.NODE_ENV === "development"
    ? "/sitecore/api/learn/neptune/wizard"
    : `${process.env.NEXT_PUBLIC_BASE_URL}/sitecore/api/learn/neptune/wizard`;

export interface FetchInternetOffersParams {
  salesSessionId: string;
  signal?: AbortSignal;
}

export interface FetchInternetOffersResult {
  data: InternetOffersApiResponse | null;
  error: Error | null;
}

/**
 *
 * @param params - Object containing salesSessionId (RC.SP value from SC cookie)
 * @returns Promise resolving to offers data or error
 *
 */
export async function fetchInternetOffers(
  params: FetchInternetOffersParams
): Promise<FetchInternetOffersResult> {
  const { salesSessionId, signal } = params;

  if (!salesSessionId) {
    return {
      data: null,
      error: new Error("Sales session ID is required"),
    };
  }

  try {
    const url = `${OFFERS_BASE_URL}/internetofferpackages?salesSessionId=${encodeURIComponent(salesSessionId?.toLowerCase())}`;

    const response = await fetch(url, {
      headers: {
        Accept:
          "text/html,application/xhtml+xml,application/xml;q=0.9,image/avif,image/webp,image/apng,*/*;q=0.8,application/signed-exchange;v=b3;q=0.7",
      },
      signal,
    });

    if (!response.ok) {
      throw new Error(
        `Failed to fetch internet offers: ${response.status} ${response.statusText}`
      );
    }

    const data: InternetOffersApiResponse = await response.json();

    if (!data.Data || !Array.isArray(data.Data.Offers)) {
      throw new Error("Invalid API response structure");
    }

    return {
      data,
      error: null,
    };
  } catch (error) {
    return {
      data: null,
      error:
        error instanceof Error ? error : new Error("Unknown error occurred"),
    };
  }
}
